/**
 * Maps the Scorecard step's form values to the API request shape.
 *
 * Backend permit list keys:
 *   scorecard_required, scorecard_config: { is_feedback_mandatory, questions[] },
 *   scorecard_feedback_description, scorecard_rating_description, fill_with_ai_enabled,
 *   ats_scorecard_template_job_id
 */
import type { ScorecardShape } from './types';

export function mapScorecardToApi(
  scorecard: Record<string, unknown>
): Record<string, unknown> {
  const s = scorecard as ScorecardShape;
  const out: Record<string, unknown> = {};

  if (typeof s.enabled === 'boolean') {
    out.scorecard_required = s.enabled;
  }

  if (typeof s.ai_enabled === 'boolean') {
    out.fill_with_ai_enabled = s.ai_enabled;
  }

  const hasQuestions = Array.isArray(s.questions) && s.questions.length > 0;
  if (hasQuestions || typeof s.is_feedback_mandatory === 'boolean') {
    out.scorecard_config = {
      is_feedback_mandatory: Boolean(s.is_feedback_mandatory),
      questions: (s.questions ?? []).map((q) => ({
        type: q.type ?? '',
        question: q.question ?? '',
        ...(q.description ? { description: q.description } : {}),
        ...(typeof q.mandatory === 'boolean' ? { mandatory: q.mandatory } : {}),
        ...(Array.isArray(q.options) && q.options.length > 0
          ? { options: q.options }
          : {}),
      })),
    };
  }

  // Records which template the questions came from so a reopen can reselect it.
  // Always sent, null included: omitting it would leave a partial update
  // holding the previously recorded source after the user cleared the picker.
  out.ats_scorecard_template_job_id =
    Number((s as { template_id?: unknown }).template_id) || null;

  if (typeof s.feedbackDescription === 'string' && s.feedbackDescription) {
    out.scorecard_feedback_description = s.feedbackDescription;
  }
  if (typeof s.ratingDescription === 'string' && s.ratingDescription) {
    out.scorecard_rating_description = s.ratingDescription;
  }

  return out;
}
